import { useState } from "react";

import { RealDashboardCanvas } from "@/pages/builder/diagram/RealDashboardCanvas";
import { SectionEditor } from "@/pages/builder/editors/SectionEditor";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";

interface DashboardTabProps {
  draft: UseBuilderDraft;
}

/**
 * Two-pane Dashboard tab (Iter 21 refactor):
 *
 *   ┌──────────────────────────┬──────────────────────────┐
 *   │  RealDashboardCanvas     │  SectionEditor           │
 *   │  (real portal render +   │  (read-only inspector +  │
 *   │   filter bar + select)   │   inline bilingual edits)│
 *   └──────────────────────────┴──────────────────────────┘
 *
 * Default layout: 50/50 inside the available content area (the app's
 * left sidebar — Reports/Projects/Components/Prompts — already claims
 * its space, so the two columns split the remainder).
 *
 * Iter 22 — added a per-canvas "expand" toggle. When the admin wants
 * to see the dashboard at full fidelity (e.g. eyeballing chart
 * proportions), they click the maximize affordance and the inspector
 * pane hides → canvas takes 100% of the content area. Click again to
 * restore the 50/50 split. State is local to this component so it
 * doesn't persist across navigations.
 *
 * Why 50/50 by default:
 * - Canvas was previously 67% but used to render abstract Text/Chart/
 *   Table cards. Iter 21 replaces those with the actual portal render
 *   (KPICard / GenericChart / DataTable with live data) — same
 *   fidelity as PreviewModal.
 * - Inspector was 33% but contained heavy form editors (chart-type
 *   picker with 20+ buttons, KPI variant selector, format dropdown,
 *   etc.) that no longer have a purpose since structural edits flow
 *   through Claude. The inspector is now a slim read-only summary
 *   plus inline editors only for bilingual labels + synonyms.
 *
 * Scroll guard: each column wrapper has ``min-h-0 h-full
 * overflow-hidden`` so the inner panes manage their own
 * ``overflow-y-auto`` scrolling independently.
 */
export function DashboardTab({ draft }: DashboardTabProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={
        expanded
          ? "grid h-full grid-cols-1"
          : "grid h-full grid-cols-1 md:grid-cols-2"
      }
    >
      <div className="min-h-0 h-full overflow-hidden">
        <RealDashboardCanvas
          draft={draft}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((v) => !v)}
        />
      </div>
      {!expanded && (
        <div className="min-h-0 h-full overflow-hidden">
          <SectionEditor draft={draft} />
        </div>
      )}
    </div>
  );
}
